import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaBuilding, FaPlus } from "react-icons/fa";
import { Alert } from "@mui/material";
import useAuth from "../useAuth";
import usePermission from "../usePermission";
import { useBelowFirmsData } from "../useBelowFirmsData";
import { useDebounce } from "../useDebounce";
import useFirmBusy from "../dealerRoles/useFirmBusy";
import {
  useSearchUnlinkedFirmsMutation,
  useLinkSubFirmMutation,
  useUnlinkSubFirmMutation,
  useSendSubFirmQuotaMutation,
  useLazyGetSendableSubFirmQuotaQuery,
} from "../../store";
import ConfirmationModal from "../../Components/Firm/Tabs/ConfirmationModal";
import SubFirmQuotaDrawer from "../../Components/Firm/Tabs/SubFirmQuotaDrawer";
import { firmProductQuotaSummary } from "../../Components/Firm/Tabs/QuotaSplitCards";

/**
 * ALT FİRMA İŞLEMLERİ (#324) — eskiden yalnız "Alt Firmalar" sekmesinin
 * (SubFirmsTab) içindeydi; merkezde Kota & Cihazlar sayfasının firma grup
 * satırları da aynı işlemleri kullanır. Davranış sekmedekiyle birebir:
 *  - satırlar: alt firmalar / alt bayiler (+ bayide müşteriler, `customersSource`)
 *  - Alt Firma Ekle (arama + onay), Bağı Kopar (onay), Kota Ver / Kota Al drawer'ı
 *  - meşguliyet (#303): üst firma meşgulse tüm satırlar kilitli
 *  - bağla / kopar sonrası satır ANINDA eklenir / düşer (sunucu listesi örtüyü temizler)
 *
 * Bayi paneli SubFirmsTab'ı `externalData` / `externalHooks` ile kullanır — aynen geçer.
 *
 * customersSource: { customers, loading, refetch, canSendQuota, canUnlink,
 *   unlink(customer) → Promise, unlinkLoading, limitToPool, requireLiveSource }
 *
 * Dönen `renderLayer()` uyarıyı, onay modallarını ve kota drawer'ını çizer;
 * `renderLinkButton()` "Alt Firma Ekle" düğmesini ve arama listesini.
 */
export default function useSubFirmActions({
  firm,
  externalData,
  externalHooks,
  customersSource = null,
  includeSubDealers = true,
  onChanged,
  // false → hiçbir istek atılmaz (alt firma / müşteri izni olmayan kullanıcı;
  // sendableSubFirmQuota firms.belowFirms | firms.customers ister, #414)
  enabled = true,
}) {
  const { auth } = useAuth();
  const { hasPermission } = usePermission();
  const [alert, setAlert] = useState(null);
  const isDealerFirm = firm?.firmType === "dealer";
  // Bayi kuralları (#303): Kota Ver yalnız havuzdan, Kota Al canlı okumalı.
  const isDealerUser = auth?.role === "dealer";

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const [dropdownResults, setDropdownResults] = useState([]);
  const dropdownRef = useRef(null);
  const debouncedDropdownSearch = useDebounce(dropdownSearch, 400);

  const [confirmLink, setConfirmLink] = useState(null);
  const [confirmUnlink, setConfirmUnlink] = useState(null);
  // { firm, direction: "toSub" | "toParent" }
  const [quotaDrawer, setQuotaDrawer] = useState(null);

  const canLink = enabled && hasPermission("firms.belowFirmsLink");
  const canUnlink = hasPermission("firms.belowFirmsUnlink");
  const canSendQuota = hasPermission(
    auth?.role === "dealer" ? "dealers.sendSubFirmQuota" : "firms.sendSubFirmQuota",
  );

  const internalData = useBelowFirmsData(
    externalData || !includeSubDealers || !enabled
      ? { id: null, token: null }
      : { id: firm?._id, token: auth.accessToken },
  );
  const subDealerData = includeSubDealers ? externalData || internalData : {};
  const belowFirms = subDealerData.belowFirms;
  const belowFirmsIsLoading = !!subDealerData.belowFirmsIsLoading || !!customersSource?.loading;
  const belowFirmsIsFetching = !!subDealerData.belowFirmsIsFetching;

  // Bayide müşteri satırları işaretlenir (#376)
  const customerRows = useMemo(
    () => (customersSource?.customers || []).map((c) => ({ ...c, __row: "customer" })),
    [customersSource?.customers],
  );
  const isCustomerRow = (row) => row?.__row === "customer";
  const canSendCustomerQuota = !!customersSource?.canSendQuota;
  const canUnlinkCustomer = !!customersSource?.canUnlink;

  const [searchUnlinkedInt, { isLoading: searchLoadingInt }] = useSearchUnlinkedFirmsMutation();
  const [linkSubFirmInt, { isLoading: linkLoadingInt }] = useLinkSubFirmMutation();
  const [unlinkSubFirmInt, { isLoading: unlinkLoadingInt }] = useUnlinkSubFirmMutation();
  const [sendQuotaInt, { isLoading: sendQuotaLoadingInt }] = useSendSubFirmQuotaMutation();
  const [fetchSendableInt, { data: sendableInt, isFetching: sendableFetchingInt }] =
    useLazyGetSendableSubFirmQuotaQuery();

  const searchUnlinked = externalHooks?.searchUnlinked || searchUnlinkedInt;
  const searchLoading = externalHooks?.searchLoading ?? searchLoadingInt;
  const linkSubFirm = externalHooks?.linkSubFirm || linkSubFirmInt;
  const linkLoading = externalHooks?.linkLoading ?? linkLoadingInt;
  const unlinkSubFirm = externalHooks?.unlinkSubFirm || unlinkSubFirmInt;
  const unlinkLoading = externalHooks?.unlinkLoading ?? unlinkLoadingInt;
  const sendQuota = externalHooks?.sendQuota || sendQuotaInt;
  const sendQuotaLoading = externalHooks?.sendQuotaLoading ?? sendQuotaLoadingInt;
  const fetchSendable = externalHooks?.fetchSendable || fetchSendableInt;
  const sendableResponse = externalHooks?.sendable ?? sendableInt;
  const sendableFetching = externalHooks?.sendableFetching ?? sendableFetchingInt;

  // Firmanın KENDİ ürün kota özeti — "Kota Ver" drawer'ına gönderilebilir bakiye
  const sendableQuota = useMemo(() => sendableResponse?.data || [], [sendableResponse]);

  const refreshSendable = () => {
    if (enabled && firm?._id && auth?.accessToken) fetchSendable({ id: firm._id, token: auth.accessToken });
  };

  useEffect(() => {
    refreshSendable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firm?._id, auth?.accessToken, enabled]);

  // Bağ koparma/kurma sonrası liste ANINDA güncellenir
  const [removedIds, setRemovedIds] = useState([]);
  const [addedFirms, setAddedFirms] = useState([]);

  useEffect(() => {
    const serverIds = new Set([...(belowFirms || []), ...customerRows].map((f) => f._id));
    setRemovedIds((prev) => {
      const next = prev.filter((id) => serverIds.has(id));
      return next.length === prev.length ? prev : next;
    });
    setAddedFirms((prev) => {
      const next = prev.filter((f) => !serverIds.has(f._id));
      return next.length === prev.length ? prev : next;
    });
  }, [belowFirms, customerRows]);

  const visibleFirms = useMemo(() => {
    const subDealerIds = new Set((belowFirms || []).map((f) => String(f._id)));
    return [
      ...(belowFirms || []).filter((f) => !removedIds.includes(f._id)),
      ...addedFirms,
      // Müşteriler alt bayilerin ardından; aynı firma iki kez listelenmez
      ...customerRows.filter((c) => !subDealerIds.has(String(c._id)) && !removedIds.includes(c._id)),
    ];
  }, [belowFirms, addedFirms, customerRows, removedIds]);

  // Firma meşguliyeti (#303) — üst firma meşgulse tüm satırlar da kilitli
  const { busyIds: rawBusyIds, isBusy } = useFirmBusy([
    firm?._id,
    ...(visibleFirms || []).map((f) => f._id),
  ]);
  const parentBusy = isBusy(firm?._id);
  const busyIds = parentBusy
    ? new Set((visibleFirms || []).map((f) => String(f._id)))
    : rawBusyIds;

  useEffect(() => {
    if (!alert) return undefined;
    const timer = setTimeout(() => setAlert(null), 5000);
    return () => clearTimeout(timer);
  }, [alert]);

  useEffect(() => {
    if (!debouncedDropdownSearch || debouncedDropdownSearch.length < 2) {
      setDropdownResults([]);
      return;
    }
    const doSearch = async () => {
      try {
        const result = await searchUnlinked({
          token: auth.accessToken,
          search: debouncedDropdownSearch,
          parentFirmId: firm._id,
        }).unwrap();
        setDropdownResults(result.data || []);
      } catch {
        setDropdownResults([]);
      }
    };
    doSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedDropdownSearch]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLinkFirm = async (subFirmId) => {
    try {
      await linkSubFirm({ token: auth.accessToken, subFirmId, parentFirmId: firm._id }).unwrap();
      setAlert({ severity: "success", message: "Firma alt firma olarak başarıyla bağlandı" });
      const linked = dropdownResults.find((f) => f._id === subFirmId);
      if (linked) setAddedFirms((prev) => [...prev, linked]);
      setDropdownResults((prev) => prev.filter((f) => f._id !== subFirmId));
      setConfirmLink(null);
      onChanged?.();
    } catch (err) {
      setAlert({ severity: "error", message: err?.data?.message || "Bağlama sırasında hata oluştu" });
      setConfirmLink(null);
    }
  };

  const handleUnlinkFirm = async (subFirmId) => {
    // Müşteri satırı: bayi bağını koparır (#202)
    if (confirmUnlink && isCustomerRow(confirmUnlink)) {
      try {
        await customersSource.unlink(confirmUnlink);
        setAlert({ severity: "success", message: `${confirmUnlink.firmName} firmasının bayi bağı koparıldı` });
        setRemovedIds((prev) => [...prev, subFirmId]);
        setConfirmUnlink(null);
        customersSource.refetch?.();
        refreshSendable();
        onChanged?.();
      } catch (err) {
        setAlert({ severity: "error", message: err?.data?.message || "Bayi bağı koparılamadı" });
        setConfirmUnlink(null);
      }
      return;
    }
    try {
      await unlinkSubFirm({ token: auth.accessToken, subFirmId }).unwrap();
      setAlert({ severity: "success", message: "Alt firma başarıyla çıkarıldı" });
      setRemovedIds((prev) => [...prev, subFirmId]);
      setConfirmUnlink(null);
      onChanged?.();
    } catch (err) {
      setAlert({ severity: "error", message: err?.data?.message || "Çıkarma sırasında hata oluştu" });
      setConfirmUnlink(null);
    }
  };

  // GERİ AL yönünde kaynak ALT firmadır; özeti satırın kendi verisinden
  const subFirmQuotaSummary = useMemo(() => firmProductQuotaSummary(quotaDrawer?.firm), [quotaDrawer]);

  const handleTransferQuota = async ({ subFirmId, productName, quota, note, direction }) => {
    try {
      const res = await sendQuota({
        token: auth.accessToken,
        id: firm._id,
        subFirmId,
        productName,
        quota,
        note: note || "",
        direction,
      }).unwrap();
      setAlert({ severity: "success", message: res?.message || "Kota başarıyla aktarıldı" });
      if (isCustomerRow(quotaDrawer?.firm)) customersSource?.refetch?.();
      setQuotaDrawer(null);
      refreshSendable();
      onChanged?.();
    } catch (err) {
      setAlert({ severity: "error", message: err?.data?.message || "Kota aktarılırken hata oluştu" });
    }
  };

  const canSendQuotaFor = (row) => (isCustomerRow(row) ? canSendCustomerQuota : canSendQuota);
  const canUnlinkFor = (row) => (isCustomerRow(row) ? canUnlinkCustomer : canUnlink);
  const linkLabel = isDealerFirm ? "Alt Bayi Ekle" : "Alt Firma Ekle";

  const renderLinkButton = ({ compact = true, className = "" } = {}) =>
    canLink ? (
      <div className={`relative shrink-0 ${className}`} ref={dropdownRef}>
        <button
          type="button"
          onClick={() => {
            setDropdownOpen(!dropdownOpen);
            setDropdownSearch("");
            setDropdownResults([]);
          }}
          className={
            compact
              ? "flex items-center gap-1.5 px-3 py-2 bg-indigo-500 text-white rounded-xl text-xs font-semibold hover:bg-indigo-600 transition-colors"
              : "flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold transition-all whitespace-nowrap bg-violet-500 text-white hover:bg-violet-600 active:scale-[0.97]"
          }
        >
          <FaPlus className="w-3 h-3" />
          <span className={compact ? "hidden sm:inline" : ""}>{linkLabel}</span>
        </button>

        {dropdownOpen && (
          <div className="absolute right-0 top-full mt-1 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50">
            <div className="p-3 border-b border-gray-100">
              <input
                type="text"
                autoFocus
                placeholder="Firma adı, bayser no veya email ile arayın..."
                value={dropdownSearch}
                onChange={(e) => setDropdownSearch(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none"
              />
            </div>
            <div className="max-h-60 overflow-y-auto">
              {searchLoading ? (
                <div className="flex items-center justify-center py-6">
                  <div className="w-5 h-5 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : dropdownSearch.length < 2 ? (
                <p className="text-xs text-gray-400 text-center py-6">En az 2 karakter girin</p>
              ) : dropdownResults.length === 0 ? (
                <p className="text-xs text-gray-400 text-center py-6">Sonuç bulunamadı</p>
              ) : (
                <div className="py-1">
                  {dropdownResults.map((f) => (
                    <div
                      key={f._id}
                      className="flex items-center gap-2.5 px-3 py-2.5 hover:bg-indigo-50 cursor-pointer transition-colors"
                      onClick={() => setConfirmLink(f)}
                    >
                      <div className="w-7 h-7 bg-indigo-50 rounded-full flex items-center justify-center shrink-0">
                        <FaBuilding className="w-2.5 h-2.5 text-indigo-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-800 truncate">{f.firmName}</p>
                        <p className="text-[11px] text-gray-500 truncate">
                          {f.bayserNo}
                          {f.email ? ` · ${f.email}` : ""}
                        </p>
                      </div>
                      <FaPlus className="w-3 h-3 text-indigo-500 shrink-0" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    ) : null;

  const renderLayer = () => (
    <>
      {alert && (
        <div className="fixed z-50 left-1/2 top-4 -translate-x-1/2 w-[90%] max-w-md">
          <Alert
            severity={alert.severity}
            onClose={() => setAlert(null)}
            variant="filled"
            sx={{ boxShadow: "0 4px 12px rgba(0,0,0,0.15)", borderRadius: "12px", fontSize: "13px" }}
          >
            {alert.message}
          </Alert>
        </div>
      )}

      <ConfirmationModal
        open={!!confirmLink}
        onClose={() => setConfirmLink(null)}
        onConfirm={() => confirmLink && handleLinkFirm(confirmLink._id)}
        title={linkLabel}
        message={confirmLink ? `${confirmLink.firmName} firmasını alt firma olarak bağlamak istediğinize emin misiniz?` : ""}
        confirmText="Ekle"
        cancelText="İptal"
        isLoading={linkLoading}
      />

      <SubFirmQuotaDrawer
        open={!!quotaDrawer}
        onClose={() => setQuotaDrawer(null)}
        onSubmit={handleTransferQuota}
        subFirm={quotaDrawer?.firm}
        direction={quotaDrawer?.direction || "toSub"}
        parentFirmName={firm?.firmName}
        sendableQuota={sendableQuota}
        subFirmQuota={subFirmQuotaSummary}
        summaryLoading={sendableFetching}
        isLoading={sendQuotaLoading}
        limitToPool={isCustomerRow(quotaDrawer?.firm) ? !!customersSource?.limitToPool : isDealerUser}
        requireLiveSource={isCustomerRow(quotaDrawer?.firm) ? !!customersSource?.requireLiveSource : isDealerUser}
      />

      <ConfirmationModal
        open={!!confirmUnlink}
        onClose={() => setConfirmUnlink(null)}
        onConfirm={() => confirmUnlink && handleUnlinkFirm(confirmUnlink._id)}
        title={isCustomerRow(confirmUnlink) ? "Bayi Bağını Kopar" : "Alt Firmayı Çıkar"}
        message={
          !confirmUnlink
            ? ""
            : isCustomerRow(confirmUnlink)
              ? `${confirmUnlink.firmName} firmasının ${firm?.firmName} bayisiyle bağını koparmak istediğinize emin misiniz? Firma bu bayinin müşteri listesinden çıkar; kotası ve makineleri firmada kalır.`
              : `${confirmUnlink.firmName} firmasını alt firma listesinden çıkarmak istediğinize emin misiniz?`
        }
        confirmText={isCustomerRow(confirmUnlink) ? "Bağı Kopar" : "Çıkar"}
        cancelText="İptal"
        isLoading={unlinkLoading || !!customersSource?.unlinkLoading}
      />
    </>
  );

  return {
    isDealerFirm,
    visibleFirms,
    isLoading: belowFirmsIsLoading,
    isFetching: belowFirmsIsFetching,
    sendableQuota,
    sendableFetching,
    busyIds,
    parentBusy,
    isCustomerRow,
    canLink,
    canSendQuotaFor,
    canUnlinkFor,
    canSendAny: canSendQuota || canSendCustomerQuota,
    canUnlinkAny: canUnlink || canUnlinkCustomer,
    openSendQuota: (row) => setQuotaDrawer({ firm: row, direction: "toSub" }),
    openTakeBackQuota: (row) => setQuotaDrawer({ firm: row, direction: "toParent" }),
    askUnlink: setConfirmUnlink,
    linkLabel,
    renderLinkButton,
    renderLayer,
  };
}
